/**
 * Lazy-loaded icons with encapsulated Suspense boundaries.
 */
import React, { Suspense } from 'react';

// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint } from '@atlassian/react-loosely-lazy';

import { withBrandIcon, type BrandIcon } from '../../../icons/with-brand-icon';

const OfficialIcon = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "@atlassian/logo-third-party/gitlab" */ '@atlassian/logo-third-party/gitlab'
		).then((m) => m.GitlabIcon),
	{ moduleId: '@atlassian/logo-third-party/gitlab' },
);

const LazyGitlabIconComponent = lazyForPaint(
	() => import(/* webpackChunkName: "icon-gitlab" */ './utils').then((m) => m.GitlabIcon),
	{ moduleId: '@atlassian/rovo-connectors-config/gitlab/icon' },
);

const LazyGitlabSearchResultIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-gitlab-search-result" */ './utils').then(
			(m) => m.GitlabSearchResultIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/gitlab/search-result-icon' },
);

const LazyGitlabIconBorderlessComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-gitlab-borderless" */ './utils').then(
			(m) => m.GitlabIconBorderless,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/gitlab/icon-borderless' },
);

/**
 * Exported icons with self-contained Suspense boundaries.
 * Consumers can use these directly without wrapping in LazySuspense.
 */
export const GitlabIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyGitlabIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'small',
);

export const GitlabSearchResultIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyGitlabSearchResultIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'medium',
);

export const GitlabIconBorderless: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyGitlabIconBorderlessComponent />
		</Suspense>
	),
	OfficialIcon,
	'xxsmall',
);
